/* Libs */
import React from 'react';
import PropTypes from 'prop-types';
import { Link } from 'react-router-dom';
import { startCase } from 'lodash';
import { Box, Typography } from '@mui/material';
import { getFormattedDateFromIsoString } from '~/src/utils/date';
import { css } from 'aphrodite';

/* Components */
import Card from '~/src/components/Card';
import { ExpandableCard } from '~/src/components/ExpandableCard/ExpandableCard';
import { Form } from '~/src/components/Forms';
import { Small } from '~/src/components/Text';
import { Row } from '~/src/components/PageLayout/Layout';
import SingleMatterSync from '~/src/components/SingleMatterSync';
import { getCardListExcludingUntaggedFields } from './utils';
import Documents from '~/src/components/Documents';
import DocumentSets from '~/src/components/DocumentSets';
import ClioQuestionnaireSubmissions from '~/src/components/ClioQuestionnaireSubmissions';
import {
  mapDocumentToTable,
  mapDocumentSetToTable,
} from '~/src/utils/dataTransformers';

/* Styles */
import styles from './styles';

const MatterDetail = ({
  summaryFields,
  documents,
  documentSets,
  relatedContactsFields,
  clioCustomFields,
  areClioFieldsEmpty,
  entityCardFields,
  matter,
  isClioQuestionnaireEnabled,
  clioQuestionnaireSubmissions,
  clioQuestionnaireSubmissionUnmappedQuestions,
  onSynced,
}) => {
  const renderClioQuestionnaireSubmissionUnmappedQuestionList = () => (
    <Box>
      {clioQuestionnaireSubmissionUnmappedQuestions.map((d) => {
        return (
          <Box
            key={d.id}
            sx={(theme) => ({
              margin: '15px',
              marginLeft: '0px',
              marginRight: '0px',
              padding: '5px 10px',
              border: '1px solid',
              borderRadius: '5px',
              borderColor: theme.palette.grey[300],
            })}
          >
            <Box sx={{ marginBottom: '5px' }}>
              <Typography sx={{ fontSize: '14px', fontWeight: '600' }}>
                {d.question}
              </Typography>
            </Box>
            <Box sx={{ marginBottom: '5px' }}>
              <Typography sx={{ fontSize: '12px' }}>{d.response}</Typography>
            </Box>
            <Box sx={{ marginBottom: '5px', display: 'flex' }}>
              <Box>
                <Typography sx={{ fontSize: '10px', color: 'gray' }}>
                  created at: {getFormattedDateFromIsoString(d.created_at)}
                </Typography>
              </Box>

              <Box sx={{ margin: '0 5px', fontSize: '10px' }}>|</Box>

              <Box>
                <a
                  href={`/clients/questionnaires/${d.questionnaire_id}/responses/${d.questionnaire_submission_id}`}
                  target="_blank"
                  rel="noreferrer"
                >
                  <Typography sx={{ fontSize: '10px', color: 'blue' }}>
                    View questionnaire
                  </Typography>
                </a>
              </Box>
            </Box>
          </Box>
        );
      })}
    </Box>
  );

  const renderEntityCardList = () => {
    const cardListExcludingUntaggedFields =
      getCardListExcludingUntaggedFields(entityCardFields);
    const entityCardList = Object.keys(cardListExcludingUntaggedFields).map(
      (entity) => {
        const label = startCase(entity);
        const fields = entityCardFields[entity];
        return (
          <Card
            elevate
            key={label}
            dynamic
            cssStyle={styles.card}
            renderHeader={() => label}
          >
            <Form fields={fields} />
          </Card>
        );
      },
    );

    return entityCardList;
  };

  return (
    <Row>
      <div className={css(styles.column)}>
        {matter.externalMatterId ? (
          <SingleMatterSync
            analyticsEventName="Matter Detail"
            onSynced={onSynced}
            matter={matter}
            cardStyle={styles.card}
          />
        ) : null}

        <Card
          elevate
          dynamic
          cssStyle={styles.card}
          renderHeader={() => 'Summary'}
        >
          <Form fields={summaryFields} />
        </Card>

        <ExpandableCard
          cardProps={{
            sx: {
              marginBottom: 2,
              maxWidth: 600,
              width: '100%',
              display: 'inline-flex',
              flexDirection: 'column',
              textAlign: 'left',
            },
          }}
          header={<Typography variant="subheading">Documents</Typography>}
          defaultExpanded={documents.length > 0}
        >
          {documents.length <= 0 && (
            <Small>
              There are no documents attached to this matter. Select a template
              for the <Link to="/templates">list of templates</Link> and choose
              this matter in the following screen.
            </Small>
          )}
          {documents.length > 0 && (
            <Documents
              documents={documents.map(mapDocumentToTable)}
              visibleItems={documents.length}
              columnWidths={['40%', '20%']}
              hideHeader
              noBorder
            />
          )}
        </ExpandableCard>

        <ExpandableCard
          cardProps={{
            sx: {
              marginBottom: 2,
              maxWidth: 600,
              width: '100%',
              display: 'inline-flex',
              flexDirection: 'column',
              textAlign: 'left',
            },
          }}
          header={<Typography variant="subheading">Document sets</Typography>}
          defaultExpanded={documentSets.length > 0}
        >
          {documentSets.length <= 0 && (
            <Small>
              There are no document sets attached to this matter. Select a
              template for the <Link to="/templates">list of templates</Link>{' '}
              and choose this matter in the following screen.
            </Small>
          )}
          {documentSets.length > 0 && (
            <DocumentSets
              documentSets={documentSets.map(mapDocumentSetToTable)}
              visibleItems={documentSets.length}
              columnWidths={['40%', '20%']}
              hideHeader
              noBorder
            />
          )}
        </ExpandableCard>

        {isClioQuestionnaireEnabled && (
          <ExpandableCard
            cardProps={{
              sx: {
                marginBottom: 2,
                maxWidth: 600,
                width: '100%',
                display: 'inline-flex',
                flexDirection: 'column',
                textAlign: 'left',
              },
            }}
            header={
              <Typography variant="subheading">Live questionnaires</Typography>
            }
            defaultExpanded={clioQuestionnaireSubmissions.length > 0}
          >
            {clioQuestionnaireSubmissions.length <= 0 && (
              <Small>
                There are no live questionnaires attached to this matter.
              </Small>
            )}
            {clioQuestionnaireSubmissions.length > 0 && (
              <ClioQuestionnaireSubmissions
                clioQuestionnaireSubmissions={clioQuestionnaireSubmissions}
                visibleItems={clioQuestionnaireSubmissions.length}
                columnWidths={['40%', '20%']}
                hideHeader
                noBorder
              />
            )}
          </ExpandableCard>
        )}

        {isClioQuestionnaireEnabled &&
          clioQuestionnaireSubmissionUnmappedQuestions?.length > 0 && (
            <ExpandableCard
              key="unmapped-questions"
              cardProps={{
                sx: {
                  marginBottom: 2,
                  maxWidth: 600,
                  width: '100%',
                  display: 'inline-flex',
                  flexDirection: 'column',
                  textAlign: 'left',
                },
              }}
              header={
                <Typography variant="subheading">
                  Questionnaire Responses
                </Typography>
              }
              defaultExpanded
            >
              {renderClioQuestionnaireSubmissionUnmappedQuestionList()}
            </ExpandableCard>
          )}
      </div>
      <div className={css(styles.divider)} />
      <div className={css(styles.column)}>
        {relatedContactsFields.length > 0 && (
          <ExpandableCard
            key="related-contacts"
            cardProps={{ sx: { marginBottom: 2, maxWidth: 600 } }}
            header={
              <Typography variant="subheading">Related Contacts</Typography>
            }
            defaultExpanded
          >
            <Form fields={relatedContactsFields} />
          </ExpandableCard>
        )}
        {clioCustomFields.length > 0 && (
          <ExpandableCard
            key="clio-fields"
            cardProps={{ sx: { marginBottom: 2, maxWidth: 600 } }}
            header={
              <Typography variant="subheading">
                Clio Manage Matter Fields
              </Typography>
            }
            defaultExpanded={!areClioFieldsEmpty}
          >
            <Form fields={clioCustomFields} />
          </ExpandableCard>
        )}

        {renderEntityCardList()}
      </div>
    </Row>
  );
};

MatterDetail.defaultProps = {
  documents: [],
  clioQuestionnaireSubmissions: [],
};

MatterDetail.propTypes = {
  documentSets: PropTypes.array,
};

export default MatterDetail;
